import {useCallback, useEffect, useRef, useState, useSyncExternalStore} from "react";
import type {RenderedFailureKey} from "utils/renderedFailure";
import {
	DEFAULT_TTL_MS,
	resourceEntry,
	startRevalidate,
	subscribeResource,
} from "./resourceCache";

/**
 * The React adapter over `./resourceCache` (#165, split in ui#617). Everything about WHAT is cached,
 * when it is stale and how a mutation rolls back lives in the store; this file only wires a component
 * to it. Import the store directly from `api/resourceCache` unless you also want one of these hooks —
 * coming through here drags React into the importer's module graph.
 */

export type CachedResourceOptions = {
	/** Freshness window in ms (default {@link DEFAULT_TTL_MS}). */
	ttlMs?: number;
	/** Called once per FAILED fetch attempt (initial, background or manual) — e.g. an error toast. */
	onError?: (messageKey: RenderedFailureKey) => void;
	/**
	 * When false the hook reads the cache but fetches nothing (default true): for a resource only worth its
	 * request once the reader asks for it, such as a transfer's files behind a collapsed row
	 * (kitecorp/transferify-api-service#2163).
	 */
	enabled?: boolean;
};

export type CachedResourceResult<T> = {
	/** The cached data — present from the very first render when the cache holds the key. */
	data: T | undefined;
	/** True only while loading WITHOUT anything to paint (cache miss, or a retry after a failure). */
	loading: boolean;
	/** Catalog key of the last failed load, or null. Set alongside stale data — never instead of it. */
	error: RenderedFailureKey | null;
	/** Manual refetch (retry); deduplicated with any in-flight load. Resolves true on success. */
	revalidate: () => Promise<boolean>;
};

/**
 * Subscribes to one cached resource. Cache hits paint synchronously; a miss triggers the fetch; an
 * entry older than `ttlMs` serves stale data and revalidates in the background. A failed load stops
 * auto-retrying (recovery is the caller's explicit `revalidate()`), so errors can't turn into
 * request loops — or masquerade as an empty list.
 */
export function useCachedResource<T>(
	key: string,
	fetcher: () => Promise<T>,
	options: CachedResourceOptions = {},
): CachedResourceResult<T> {
	const {ttlMs = DEFAULT_TTL_MS, onError, enabled = true} = options;
	// Callers pass inline closures — track the latest without re-subscribing or refetching.
	const fetcherRef = useRef(fetcher);
	fetcherRef.current = fetcher;
	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;

	const entry = useSyncExternalStore(
		useCallback(callback => subscribeResource(key, callback), [key]),
		() => resourceEntry<T>(key),
		// SSG/SSR snapshot: private panels never pre-render data.
		() => undefined,
	);

	const revalidate = useCallback(
		() => startRevalidate<T>(key, () => fetcherRef.current(), message => onErrorRef.current?.(message)),
		[key],
	);

	// Load policy: miss → fetch; failed → wait for an explicit retry; stale → background revalidate.
	useEffect(() => {
		if (!enabled || entry?.error !== undefined) {
			return;
		}
		if (entry?.data === undefined || Date.now() - entry.updatedAt > ttlMs) {
			void revalidate();
		}
	}, [enabled, entry, ttlMs, revalidate]);

	return {
		data: entry?.data,
		// A disabled reader is not loading: it asked for nothing.
		loading: enabled && (entry === undefined || (entry.data === undefined && entry.error === undefined)),
		error: entry?.error ?? null,
		revalidate,
	};
}

/**
 * In-flight mutation counter for the `pending` contract flag: `pending` is true while ANY tracked
 * action is running, so views can disable Save/Delete and block double submits during optimistic
 * mutations. `track` forwards the promise's result/rejection untouched.
 */
export function usePending(): {pending: boolean; track: <R>(action: Promise<R>) => Promise<R>} {
	const [count, setCount] = useState(0);
	const track = useCallback(async <R, >(action: Promise<R>): Promise<R> => {
		setCount(current => current + 1);
		try {
			return await action;
		} finally {
			setCount(current => current - 1);
		}
	}, []);
	return {pending: count > 0, track};
}
